import { URLQueryParamNames } from '@src/types/urlQueryParamNames';

import { FagomradeType, MalgruppeType, type QueryMapSetup, SorteringType, VerktoyType } from '../types/minhelseentities';
import type { HNVerktoyFrontend } from '../types/Resources';

export const getFagomraadeTyperQueryMaps = (resource?: HNVerktoyFrontend): QueryMapSetup[] => {
  const fagomraader: QueryMapSetup[] = [];

  const fagomraderValues: number[] = Object.values(FagomradeType).filter(value => typeof value === 'number');

  fagomraderValues.forEach(value => {
    const name = FagomradeType[value] as string;
    const query = convertToQueryMapSetup(value.toString(), name, getFagomraadeVisningTekst(value, resource));
    fagomraader.push(query);
  });
  return fagomraader;
};

export const getVerktoyTyperQueryMaps = (resource?: HNVerktoyFrontend): QueryMapSetup[] => {
  const verktoytyper: QueryMapSetup[] = [];
  const verktoytyperValues: number[] = Object.values(VerktoyType).filter(value => typeof value === 'number');

  verktoytyperValues.forEach(value => {
    const name = VerktoyType[value] as string;
    const query = convertToQueryMapSetup(value.toString(), name, getVerktoyTyperVisningTekst(value, resource));
    verktoytyper.push(query);
  });
  return verktoytyper;
};

export const getMalgrupperQueryMaps = (resource?: HNVerktoyFrontend): QueryMapSetup[] => {
  const malgrupper: QueryMapSetup[] = [];
  const malgrupperValues: number[] = Object.values(MalgruppeType).filter(value => typeof value === 'number');

  malgrupperValues.forEach(value => {
    const name = MalgruppeType[value] as string;
    const query = convertToQueryMapSetup(value.toString(), name, getMalgrupperVisningTekst(value, resource));
    malgrupper.push(query);
  });
  return malgrupper;
};

export const getSorteringQueryMaps = (): QueryMapSetup[] => {
  const sortering: QueryMapSetup[] = [];
  const sorteringValues: number[] = Object.values(SorteringType).filter(value => typeof value === 'number');

  sorteringValues.forEach(value => {
    const name = SorteringType[value] as string;
    const query = convertToQueryMapSetup(value.toString(), name, '');
    sortering.push(query);
  });
  return sortering;
};

const getFagomraadeVisningTekst = (value: FagomradeType | string, resource?: HNVerktoyFrontend): string => {
  if (!resource) {
    return '';
  }
  switch (value) {
    case FagomradeType.PSYKISK_HELSE:
      return resource?.fagomrade_psykiskHelse;
    case FagomradeType.SYKDOM_OG_SKADER:
      return resource?.fagomrade_sykdomOgSkader;
    case FagomradeType.LIVSSTIL_OG_TRENING:
      return resource?.fagomrade_livsstilOgTrening;
    case FagomradeType.TANKER_OG_FOLELSER:
      return resource?.fagomrade_tankerOgFolelser;
    case FagomradeType.GRAVIDITET_OG_FODSEL:
      return resource?.fagomrade_graviditetOgFodsel;
    case FagomradeType.RAAD_OG_TIPS_I_HVERDAGEN:
      return resource?.fagomrade_raadOgTipsIHverdagen;
    default:
      return '';
  }
};

const getVerktoyTyperVisningTekst = (value: VerktoyType | string, resource?: HNVerktoyFrontend): string => {
  if (!resource) {
    return '';
  }
  switch (value) {
    case VerktoyType.App:
      return resource.verktoyType_App;
    case VerktoyType.Weblosning:
      return resource.verktoyType_Weblosning;
    default:
      return '';
  }
};

const getMalgrupperVisningTekst = (value: MalgruppeType | string, resource?: HNVerktoyFrontend): string => {
  if (!resource) {
    return '';
  }
  switch (value) {
    case MalgruppeType.Barn:
      return resource.verktoyDetails_MalgruppeType_Barn;
    case MalgruppeType.Eldre:
      return resource.verktoyDetails_MalgruppeType_Eldre;
    case MalgruppeType.Ungdom:
      return resource.verktoyDetails_MalgruppeType_Ungdom;
    case MalgruppeType.Voksne:
      return resource.verktoyDetails_MalgruppeType_Voksne;
    default:
      return '';
  }
};

const convertToQueryMapSetup = (value: string, name: string | undefined, displayname: string): QueryMapSetup => {
  return {
    value: value,
    name: name,
    displayname: displayname,
  } as QueryMapSetup;
};

export const hentSokParam = (resources?: HNVerktoyFrontend): URLSearchParams => {
  const newParamsLowerCase = new URLSearchParams();
  const query = new URLSearchParams(window.location.search);
  const fagomraader = getFagomraadeTyperQueryMaps(resources);
  const verktoytyper = getVerktoyTyperQueryMaps(resources);
  const malgrupper = getMalgrupperQueryMaps(resources);
  const sortering = getSorteringQueryMaps();

  for (const [name, value] of query) {
    switch (name.toLowerCase()) {
      case URLQueryParamNames.Sok: //  SOK_QUERY:
        newParamsLowerCase.append(name.toLowerCase(), value);
        break;
      case URLQueryParamNames.Fagomrade: // FAGOMRADE_QUERY_PARAM
        fagomraader.forEach(fagomraade => {
          if (fagomraade.name.toLowerCase() === value.toLowerCase() || fagomraade.value === value) {
            newParamsLowerCase.append(name.toLowerCase(), fagomraade.value);
            return;
          }
        });
        break;
      case URLQueryParamNames.VerktoyType: // VERKTOYTYPE_QUERY_PARAM
        verktoytyper.forEach(verktoytype => {
          if (verktoytype.name.toLowerCase() === value.toLowerCase() || verktoytype.value === value) {
            newParamsLowerCase.append(name.toLowerCase(), verktoytype.value);
            return;
          }
        });
        break;
      case URLQueryParamNames.Malgruppe: // MALGRUPPE_QUERY_PARAM
        malgrupper.forEach(malgruppe => {
          if (malgruppe.name.toLowerCase() === value.toLowerCase() || malgruppe.value === value) {
            newParamsLowerCase.append(name.toLowerCase(), malgruppe.value);
            return;
          }
        });
        break;
      case URLQueryParamNames.Sortering: // SORTERING_QUERY_PARAM
        sortering.forEach(sort => {
          if (sort.name.toLowerCase() === value.toLowerCase()) {
            newParamsLowerCase.append(name.toLowerCase(), sort.name);
            return;
          }
        });
        break;
      default:
        newParamsLowerCase.append(name.toLowerCase(), value.toLowerCase());
    }
  }
  return newParamsLowerCase;
};
